import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, downloadFile } from "@/lib/api";
import { Spinner, Empty } from "@/components/common";
import DocViewer from "@/components/DocViewer";
import { SalarioCheck } from "@/components/CertificadosEmpleado";
import { Award, Eye, Download, CheckCircle2, AlertTriangle } from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" });

function ContratoCard({ k, firma, onPreview, onEmitir, busy, sal, setSal }) {
  const ok = k.completo && firma;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`cert-contrato-${k.contratacion_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-slate-800">{k.cargo || "Cargo sin registrar"}</p>
          <p className="text-sm text-slate-500">{k.empresa_usuaria || "—"} · {k.fuente === "vinculacion" ? "Vinculación registrada por HUMAN" : `Contrato ${k.numero_contrato}`}</p>
          <p className="mt-1 text-xs text-slate-400">Ingreso: {k.fecha_ingreso || "—"} · {k.estado_laboral === "retirado" ? "Retirado" : "Activo"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SalarioCheck k={k} value={sal} onChange={setSal} testid={`cert-salario-${k.contratacion_id}`} />
          <button disabled={!k.completo} onClick={onPreview} data-testid={`cert-preview-${k.contratacion_id}`} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"><Eye className="h-4 w-4" /> Vista previa</button>
          <button disabled={!ok || busy} onClick={onEmitir} data-testid={`cert-emitir-${k.contratacion_id}`} className="flex items-center gap-1.5 rounded-lg bg-vino-700 px-3 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-50">{busy ? <Spinner className="h-4 w-4" /> : <Award className="h-4 w-4" />} Solicitar certificado</button>
        </div>
      </div>
      {!k.completo && (
        <p className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid={`cert-faltantes-${k.contratacion_id}`}>
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> Aún no se puede certificar este contrato. Falta: {k.faltantes.join(", ")}. HUMAN debe completarlo.
        </p>
      )}
    </div>
  );
}

export default function PortalCertificados() {
  const [data, setData] = useState(null);
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState("");
  const [sal, setSal] = useState({});
  const load = () => api.get("/portal/certificados").then((r) => setData(r.data)).catch(() => setData({ contratos: [], certificados: [] }));
  useEffect(() => { load(); }, []);

  const emitir = async (k) => {
    setBusy(k.contratacion_id);
    try {
      const { data: rec } = await api.post("/portal/certificados", { contratacion_id: k.contratacion_id, con_salario: !!sal[k.contratacion_id] });
      toast.success(`Certificado ${rec.consecutivo} expedido. Se descargará ahora.`);
      await downloadFile(`/portal/certificados/${rec.id}/download`, `certificado_${rec.consecutivo}.pdf`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!data) return <div className="flex h-48 items-center justify-center text-vino-700"><Spinner className="h-6 w-6" /></div>;
  return (
    <div className="space-y-6" data-testid="portal-certificados">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Mis certificados</h1>
        <p className="mt-1 text-sm text-slate-500">Certificado laboral con tus datos registrados. Elige el contrato, revisa la vista previa y solicítalo con un clic.</p>
      </div>
      {!data.firma_disponible && <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="cert-sin-firma">La expedición está temporalmente deshabilitada: HUMAN aún no ha autorizado la firma institucional.</p>}
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-slate-800 md:text-lg">Certificado laboral</h2>
        {data.contratos.length === 0 ? <Empty icon={Award} title="Sin contratos para certificar" subtitle="Cuando tu contrato esté firmado en Roge podrás solicitar tu certificado aquí." />
          : data.contratos.map((k) => <ContratoCard key={k.contratacion_id} k={k} firma={data.firma_disponible} busy={busy === k.contratacion_id} sal={!!sal[k.contratacion_id]} setSal={(v) => setSal((p) => ({ ...p, [k.contratacion_id]: v }))} onPreview={() => setView(k)} onEmitir={() => emitir(k)} />)}
      </section>
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-slate-800 md:text-lg">Certificados expedidos</h2>
        {data.certificados.length === 0 ? <p className="text-sm text-slate-400">Todavía no has solicitado certificados.</p> : (
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
            {data.certificados.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3" data-testid={`cert-emitido-${c.id}`}>
                <div className="text-sm"><p className="font-semibold text-slate-800">{c.estado !== "anulado" && <CheckCircle2 className="mr-1 inline h-4 w-4 text-emerald-600" />}{c.consecutivo}{c.estado === "anulado" && <span className="ml-2 rounded bg-red-50 px-1.5 text-[11px] font-semibold text-red-700">Anulado</span>}</p><p className="text-xs text-slate-500">{c.cargo} · {c.empresa_usuaria} · {fmt(c.fecha)}</p></div>
                {c.estado !== "anulado" && <button onClick={() => downloadFile(`/portal/certificados/${c.id}/download`, `certificado_${c.consecutivo}.pdf`)} data-testid={`cert-descargar-${c.id}`} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"><Download className="h-4 w-4" /> Descargar</button>}
              </div>
            ))}
          </div>
        )}
      </section>
      {view && <DocViewer url={`/portal/certificados/preview/${view.contratacion_id}?con_salario=${!!sal[view.contratacion_id]}`} titulo="Vista previa del certificado laboral" subtitulo={`${view.cargo} · ${view.empresa_usuaria}`} nombreArchivo="vista_previa_certificado.pdf" onClose={() => setView(null)} />}
    </div>
  );
}
